import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import toast from '../../../components/ui/Toast';
import analyticsService from '../../../services/analyticsService';

const AgentControlCard = ({ agent, onToggle, disabled }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastAction, setLastAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleToggleClick = () => {
    if (agent?.isActive) {
      setShowConfirm(true);
    } else {
      handleActivation();
    }
  };

  const handleRollback = async () => {
    if (!lastAction) return;
    
    toast?.loading('Rolling back changes...');
    setIsProcessing(true);
    try {
      await onToggle();
      
      // Track rollback as error recovery
      analyticsService?.trackErrorRecovery(
        'agent_toggle_rollback',
        'User initiated rollback',
        0,
        true,
        agent?.id
      );
      
      setLastAction(null);
      toast?.dismiss();
      toast?.success('Changes rolled back successfully', {
        description: `${agent?.name} restored to previous state`
      });
    } catch (error) {
      toast?.dismiss();
      toast?.error('Rollback failed', {
        description: error?.message || 'Unable to restore previous state'
      });
      
      // Track failed rollback
      analyticsService?.trackErrorRecovery(
        'agent_toggle_rollback',
        error?.message || 'Rollback failed',
        0,
        false,
        agent?.id
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivation = async () => {
    setIsProcessing(true);
    const currentRetry = retryCount;
    
    try {
      await onToggle();
      setLastAction({ type: 'activate', agentId: agent?.id });
      setShowSuccess(true);
      
      // Track successful agent activation
      analyticsService?.trackAgentToggle(
        agent?.id,
        agent?.name,
        true,
        agent?.canActivate ? 'user_choice' : 'upgrade'
      );
      
      // If this was a retry, track successful recovery
      if (currentRetry > 0) {
        analyticsService?.trackErrorRecovery(
          'agent_activation_failed',
          'Activation succeeded after retry',
          currentRetry,
          true,
          agent?.id
        );
        setRetryCount(0);
      }
      
      toast?.success(`${agent?.name} activated successfully`, {
        description: 'Agent is now processing tasks',
        duration: 3000
      });
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      setErrorMessage(error?.message || 'Failed to activate agent');
      setShowError(true);
      
      // Track activation error
      analyticsService?.trackErrorRecovery(
        'agent_activation_failed',
        error?.message || 'Failed to activate agent',
        newRetryCount,
        false,
        agent?.id
      );
      
      toast?.error('Activation failed', {
        description: error?.message || 'Please try again',
        action: {
          label: 'Retry',
          onClick: handleActivation
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDeactivation = async () => {
    setShowConfirm(false);
    setIsProcessing(true);
    const currentRetry = retryCount;
    
    try {
      await onToggle();
      setLastAction({ type: 'deactivate', agentId: agent?.id });
      setShowSuccess(true);
      
      // Track successful agent deactivation
      analyticsService?.trackAgentToggle(
        agent?.id,
        agent?.name,
        false,
        'user_choice'
      );
      
      // If this was a retry, track successful recovery
      if (currentRetry > 0) {
        analyticsService?.trackErrorRecovery(
          'agent_deactivation_failed',
          'Deactivation succeeded after retry',
          currentRetry,
          true,
          agent?.id
        );
        setRetryCount(0);
      }
      
      toast?.success(`${agent?.name} deactivated`, {
        description: 'Agent has stopped processing tasks',
        action: {
          label: 'Undo',
          onClick: handleRollback
        },
        duration: 5000
      });
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      setErrorMessage(error?.message || 'Failed to deactivate agent');
      setShowError(true);
      
      // Track deactivation error
      analyticsService?.trackErrorRecovery(
        'agent_deactivation_failed',
        error?.message || 'Failed to deactivate agent',
        newRetryCount,
        false,
        agent?.id
      );
      
      toast?.error('Deactivation failed', {
        description: error?.message || 'Please try again'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = () => {
    setShowError(false);
    if (lastAction?.type === 'activate') {
      handleActivation();
    } else {
      confirmDeactivation();
    }
  };

  const getGradientClass = (color) => {
    const gradients = {
      purple: 'from-purple-500/10 to-purple-600/5',
      blue: 'from-blue-500/10 to-blue-600/5',
      emerald: 'from-emerald-500/10 to-emerald-600/5',
      orange: 'from-orange-500/10 to-orange-600/5',
      rose: 'from-rose-500/10 to-rose-600/5'
    };
    return gradients?.[color] || gradients?.purple;
  };

  const formatLastUsed = (dateString) => {
    if (!dateString) return 'Never used';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/50">
      {/* Header with gradient */}
      <div className={`h-2 bg-gradient-to-r ${getGradientClass(agent?.color)}`} />
      
      <div className="p-6 space-y-4">
        {/* Agent Info */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${getGradientClass(agent?.color)} flex items-center justify-center`}
            >
              <Icon
                name={agent?.icon}
                size={24}
                color={`var(--color-${agent?.color || 'primary'})`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground mb-1 font-heading">
                {agent?.name}
              </h3>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {agent?.description}
              </p>

              {/* Agent Analytics */}
              {agent?.analytics && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Monthly Interactions</span>
                    <span className="font-semibold text-foreground">
                      {agent?.analytics?.monthlyInteractions || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Efficiency</span>
                    <span className="font-semibold text-foreground">
                      {agent?.analytics?.efficiencyPercentage || 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Adoption Trend</span>
                    <div className="flex items-center gap-1">
                      {agent?.analytics?.adoptionTrend?.direction === 'up' && (
                        <Icon name="TrendingUp" size={12} className="text-success" />
                      )}
                      {agent?.analytics?.adoptionTrend?.direction === 'down' && (
                        <Icon name="TrendingDown" size={12} className="text-error" />
                      )}
                      {agent?.analytics?.adoptionTrend?.direction === 'stable' && (
                        <Icon name="Minus" size={12} className="text-muted-foreground" />
                      )}
                      <span className={`font-semibold ${
                        agent?.analytics?.adoptionTrend?.direction === 'up' ? 'text-success' :
                        agent?.analytics?.adoptionTrend?.direction === 'down' ? 'text-error' : 'text-muted-foreground'
                      }`}>
                        {agent?.analytics?.adoptionTrend?.percentage > 0 ? '+' : ''}
                        {agent?.analytics?.adoptionTrend?.percentage || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status and Toggle */}
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                agent?.isActive ? 'bg-green-500' : 'bg-gray-400'
              }`}
            />
            <span className="text-sm font-medium text-foreground">
              {agent?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          <button
            onClick={handleToggleClick}
            disabled={disabled || isProcessing || (!agent?.canActivate && !agent?.isActive)}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${agent?.isActive ? 'bg-primary' : 'bg-gray-300'}
            `}
            aria-label={`Toggle ${agent?.name}`}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${agent?.isActive ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* Last Used */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Last used:</span>
          <span className="font-medium text-foreground">
            {formatLastUsed(agent?.lastUsed)}
          </span>
        </div>

        {/* Tier Requirement Badge */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Required plan:</span>
          <span
            className={`
              inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
              ${agent?.tierRequirement?.tier === 'free' ? 'bg-gray-500/10 text-gray-500' : ''}
              ${agent?.tierRequirement?.tier === 'draftiq_plus' ? 'bg-blue-500/10 text-blue-500' : ''}
              ${agent?.tierRequirement?.tier === 'enterprise' ? 'bg-purple-500/10 text-purple-500' : ''}
            `}
          >
            {agent?.tierRequirement?.label}
          </span>
        </div>

        {/* Usage Impact */}
        {agent?.isActive && (
          <div className="p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-blue-500 mb-1">Usage Impact</p>
                <p className="text-xs text-blue-500/80">{agent?.usageImpact}</p>
              </div>
            </div>
          </div>
        )}

        {/* Locked State */}
        {!agent?.canActivate && !agent?.isActive && (
          <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Lock" size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-amber-500 mb-1">Upgrade Required</p>
                <p className="text-xs text-amber-500/80">
                  Requires {agent?.tierRequirement?.label} plan or higher
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={`Deactivate ${agent?.name}?`}
        description="This agent will stop processing tasks and won't be available until reactivated."
        icon="AlertTriangle"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => setShowConfirm(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              fullWidth
              onClick={confirmDeactivation}
              disabled={isProcessing}
            >
              {isProcessing ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg">
            <p className="text-xs font-medium text-amber-500 mb-1">Impact Assessment</p>
            <p className="text-xs text-amber-500/80">{agent?.usageImpact}</p>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Operation Successful"
        description={`${agent?.name} has been ${lastAction?.type === 'activate' ? 'activated' : 'deactivated'} successfully.`}
        icon="CheckCircle"
        actions={
          <>
            {lastAction?.type === 'deactivate' && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={handleRollback}
                iconName="RotateCcw"
                iconPosition="left"
                disabled={isProcessing}
              >
                Undo Changes
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={() => setShowSuccess(false)}
            >
              Done
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {lastAction?.type === 'deactivate' && (
            <div className="p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-500">
                  You can reactivate this agent anytime from this screen.
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showError}
        onClose={() => setShowError(false)}
        title="Operation Failed"
        description={errorMessage}
        icon="XCircle"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => setShowError(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={handleRetry}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={isProcessing}
            >
              Retry
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
            <p className="text-xs font-medium text-red-500 mb-1">Possible Causes:</p>
            <ul className="text-xs text-red-500/80 space-y-1 list-disc list-inside">
              <li>Network connection issue</li>
              <li>Subscription limit reached</li>
              <li>Agent configuration error</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentControlCard;